<template>
  <div>
    <slot
      :price="price"
      :applied-reductions="appliedReductions"
      :to-currency="toCurrency"
    >
      <slot name="relative-reductions">
        <div
          class="flex justify-start"
          :class="{
            'justify-start': isPdp,
            'justify-end': !isPdp,
          }"
        >
          <span
            v-if="showBadge && totalReductions"
            class="inline-block rounded-sm px-2 py-1 text-white"
            :class="{
              'bg-red text-sm': !blackFridayLabels,
              'bg-black !px-3 !py-2 text-xl leading-none': blackFridayLabels,
            }"
          >
            -{{ Math.round(totalReductions.relative * 100) }}%
          </span>
        </div>
      </slot>
      <p
        :class="{
          'mt-2': showBadge,
          'text-xl': size === 'xl',
          'text-sm': size === 'sm',
          'text-md': size === 'md',
          'text-md leading-[14px] lg:text-base lg:leading-[19px]':
            size === 'md' && !isPdp,
          'font-bold': type === 'loud',
          'text-red': showPriceReduction && appliedReductions.length,
          'flex flex-col-reverse': verticalAlign,
          'flex flex-col': isPdp,
        }"
      >
        <span
          :data-qa="isPdp ? 'product-price' : 'price'"
          data-test-id="product-price"
        >
          {{ toCurrency(priceWithTax) + ' ' }}
        </span>
        <span
          v-if="showPriceReduction && totalReductions.absoluteWithTax"
          :class="{
            'text-sm': size === 'md' && !isPdp,
            'text-base': size === 'xl' && isPdp,
          }"
          class="font-medium text-black line-through"
        >
          {{ toCurrency(priceWithTax + totalReductions.absoluteWithTax) }}
        </span>
        <span
          class="text-sm text-black"
          :class="{ 'flex flex-col md:inline': isPdp }"
        >
          <slot name="tax-info">
            <span v-if="showTaxInfo">
              {{ $t('price.including_vat')
              }}<span v-if="showShippingCostHint">, </span>
            </span>
          </slot>
          <slot name="shipping-costs">
            <ShippingCostHint v-if="showShippingCostHint" />
          </slot>
        </span>
      </p>
    </slot>
  </div>
</template>

<script setup lang="ts">
import type { AppliedReduction, Price } from '@scayle/storefront-nuxt'
import { toCurrency } from '~/utils/price'

interface Props {
  appliedReductions?: AppliedReduction[]
  price: Price
  showTaxInfo?: boolean
  showPriceReduction?: boolean
  showPriceReductionBadge?: boolean
  showShippingCostHint?: boolean
  size?: 'xl' | 'md' | 'sm'
  type?: 'loud' | 'whisper'
  isPdp?: boolean
  verticalAlign?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  appliedReductions: () => [],
  showTaxInfo: false,
  showPriceReduction: true,
  showPriceReductionBadge: false,
  showShippingCostHint: false,
  size: 'xl',
  type: 'loud',
  isPdp: false,
  verticalAlign: false,
})

const { blackFridayLabels } = useGlobalConfig()
const totalReductions = computed(() =>
  getTotalAppliedReductions(props.price, props.appliedReductions),
)

const showBadge = computed(
  () => props.appliedReductions && props.showPriceReductionBadge,
)

const priceWithTax = computed(() => props.price?.withTax)

// TODO: remove this function and use the one from storefront-core
const getTotalAppliedReductions = (
  price: Price,
  appliedReductions: AppliedReduction[],
) => {
  const total = { absoluteWithTax: 0, relative: 0.0 }
  if (price) {
    appliedReductions.forEach((reduction) => {
      total.absoluteWithTax += reduction.amount.absoluteWithTax
      total.relative += reduction.amount.relative
    })
  }
  return total
}
</script>
